import { CSSProperties, memo, ReactNode } from 'react';

import { FrameTier } from './model/types';

/**
 * The streak frame (canvas: AvatarFrame; EN-11164). A ring drawn OUTSIDE the
 * avatar's circle: the root box is the avatar alone, so turning the frame on,
 * off or to another tier moves nothing (EN-11164 AC 10). The band is 8.5% of
 * the diameter per side, never under 3 px, plus a 1 px outline.
 *
 * Fully static. The canvas gives Legend a glow and spark bursts; those are
 * off at the table by spec, and a permanently visible loop is exactly the
 * idle-heat pattern the performance guide warns about, so this component
 * never animates. A one-off switch flash belongs to the table celebration.
 */
export type TierStyle = {
  ring: string;
  rivets: number;
  rivet?: string;
  engrave?: boolean;
  inlay?: string;
};

export const FRAME_TIERS: Record<FrameTier, TierStyle> = {
  cowboy: {
    ring: 'conic-gradient(from 200deg, #f6c08a, #b0612a, #f0a457, #7a3a10, #f6c08a)',
    rivets: 0
  },
  grinder: {
    ring: 'conic-gradient(from 200deg, #fff4c4, #c8901a, #ffe58f, #8a5a0c, #fff4c4)',
    rivets: 4,
    rivet: '#e9c25a'
  },
  marshal: {
    ring: 'conic-gradient(from 200deg, #ffffff, #b8c0cc, #f6f8fb, #7b8492, #ffffff)',
    rivets: 8,
    rivet: '#e3e7ec',
    engrave: true
  },
  baron: {
    ring: 'conic-gradient(from 200deg, #c9d3df, #4a5563, #aab6c4, #2c333d, #c9d3df)',
    rivets: 12,
    rivet: '#cfd8e3',
    inlay: '#d9b24a'
  },
  legend: {
    ring: 'conic-gradient(from 200deg, #d8f0ff, #3a6dff, #9fd0ff, #1f2c96, #d8f0ff)',
    rivets: 16,
    rivet: '#f0c75a',
    inlay: '#e3bb4f'
  }
};

export function frameBand(size: number): number {
  return Math.max(3, Math.round(size * 0.085));
}

export type AvatarFrameProps = {
  /** Null draws no frame (no status, hidden, or the streak ended). */
  tier: FrameTier | null;
  size: number;
  /** The avatar itself, sized `size` square; or pass `src`. */
  children?: ReactNode;
  src?: string;
  alt?: string;
  /** Hide the band while the player's own turn-timer ring runs. */
  hidden?: boolean;
  className?: string;
};

function Rivets({ size, b, t }: { size: number; b: number; t: TierStyle }) {
  if (!t.rivets || size < 40) return null;
  const S = size + 2 * b;
  const inlayW = t.inlay ? Math.max(1.5, Math.round(size * 0.028 * 2) / 2) : 0;
  const bandMid = t.inlay ? (b - inlayW) / 2 : b / 2;
  const R = S / 2 - bandMid;
  const s = Math.max(2, Math.round(b * 0.42 * 10) / 10);
  const dots = [];
  for (let i = 0; i < t.rivets; i++) {
    const a = (Math.PI * 2 * i) / t.rivets + Math.PI / t.rivets;
    dots.push(
      <span
        key={i}
        style={{
          position: 'absolute',
          left: S / 2 + R * Math.cos(a) - s / 2,
          top: S / 2 + R * Math.sin(a) - s / 2,
          width: s,
          height: s,
          borderRadius: '50%',
          background: `radial-gradient(circle at 35% 30%, #ffffff 0%, ${t.rivet} 55%, #3a2a10 100%)`,
          boxShadow: '0 0.5px 1px rgba(0,0,0,0.6)'
        }}
      />
    );
  }
  return <>{dots}</>;
}

const abs: CSSProperties = { position: 'absolute', borderRadius: '50%', pointerEvents: 'none' };

function AvatarFrame({ tier, size, children, src, alt = '', hidden, className }: AvatarFrameProps) {
  const t = tier ? FRAME_TIERS[tier] : null;
  const b = t ? frameBand(size) : 0;
  return (
    <div
      className={className}
      style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      {children ?? (
        <img
          src={src}
          alt={alt}
          width={size}
          height={size}
          style={{
            display: 'block',
            width: size,
            height: size,
            borderRadius: '50%',
            objectFit: 'cover'
          }}
        />
      )}
      {t && !hidden && (
        <div aria-hidden='true' data-frame-tier={tier} style={{ ...abs, inset: -b }}>
          <div
            style={{
              ...abs,
              inset: 0,
              background: t.ring,
              WebkitMask: `radial-gradient(farthest-side, transparent calc(100% - ${b}px), #000 calc(100% - ${b}px + 0.5px))`,
              mask: `radial-gradient(farthest-side, transparent calc(100% - ${b}px), #000 calc(100% - ${b}px + 0.5px))`
            }}
          />
          <div
            style={{
              ...abs,
              inset: 0,
              boxShadow:
                'inset 0 1px 1px rgba(255,255,255,0.55), inset 0 -1px 2px rgba(0,0,0,0.5), 0 0 0 1px rgba(0,0,0,0.35)'
            }}
          />
          <div
            style={{
              ...abs,
              inset: b,
              boxShadow:
                'inset 0 1px 3px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(0,0,0,0.45), 0 0 0 0.75px rgba(255,255,255,0.3)'
            }}
          />
          {t.engrave && (
            <div
              style={{
                ...abs,
                inset: Math.round(b * 0.42),
                border: '1px solid rgba(70,78,90,0.55)',
                boxShadow: '0 1px 0 rgba(255,255,255,0.6)'
              }}
            />
          )}
          {t.inlay && (
            <div
              style={{
                ...abs,
                inset: b - Math.max(1.5, Math.round(size * 0.028 * 2) / 2),
                border: `${Math.max(1.5, Math.round(size * 0.028 * 2) / 2)}px solid ${t.inlay}`,
                boxShadow:
                  'inset 0 0 0 0.5px rgba(255,236,180,0.75), 0 0 0 0.5px rgba(90,60,10,0.6)'
              }}
            />
          )}
          <Rivets size={size} b={b} t={t} />
        </div>
      )}
    </div>
  );
}

export default memo(AvatarFrame);
